import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { API_BASE_URL, PREVIEW_SESSION_CONFIG } from '../utils/api';
import { mapWorkflowActionToApi } from '../utils/workflowActions';
import { readAdminApiErrorMessage } from '../utils/adminUi';
import FlowPreviewModal from './FlowPreviewModal';
import SearchableDropdown from './shared/SearchableDropdown';
import { showToast } from '../utils/toast';
import { trackEvent } from '../services/applicationInsights';
import {
  ORG_STORAGE_KEY,
  SITE_STORAGE_KEY,
  getInitialOrgId,
  persistOrgSelection,
  getInitialSiteId,
  persistSiteSelection,
  validateAndReconcileSelection
} from './utils/adminScopeStorage';
import {
  FaProjectDiagram,
  FaFilter,
  FaEye,
  FaTrash,
  FaCheck,
  FaTimes,
  FaSearch,
  FaSyncAlt,
  FaChevronLeft,
  FaChevronRight,
  FaCircle,
  FaFolderOpen,
  FaQuestionCircle,
  FaBan,
} from "react-icons/fa";
import { MdPreview } from "react-icons/md";

function normalizeUrl(urlOrDomain) {
  const v = (urlOrDomain ?? '').toString().trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v.replace(/\/+$/g, '');
  return `https://${v}`.replace(/\/+$/g, '');
}

function getSiteField(site, keys = []) {
  if (!site || typeof site !== 'object') return undefined;

  for (const key of keys) {
    const value = site[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return undefined;
}

function getSiteDomains(site) {
  const domainsValue = getSiteField(site, [
    'domainNames',
    'DomainNames',
    'domains',
    'Domains',
    'domainName',
    'DomainName'
  ]);

  if (Array.isArray(domainsValue)) {
    return domainsValue.filter(Boolean);
  }

  if (typeof domainsValue === 'string') {
    return domainsValue.split(',').map((d) => d.trim()).filter(Boolean);
  }

  return [];
}

function getHostUrlFromSite(site) {
  if (!site) return '';

  const previewBaseValue = getSiteField(site, [
    'previewBaseUrl',
    'PreviewBaseUrl',
    'previewUrl',
    'PreviewUrl'
  ]);
  const fromBaseUrl = normalizeUrl(previewBaseValue);
  if (fromBaseUrl) return fromBaseUrl;

  const domains = getSiteDomains(site);
  const first = domains.find(Boolean);
  return normalizeUrl(first);
}

function buildPreviewRedirectUrl({ previewBaseUrl, previewSessionId, flowId }) {
  const base = normalizeUrl(previewBaseUrl);
  const id = (previewSessionId ?? '').toString().trim();
  const targetFlowId = (flowId ?? '').toString().trim();
  if (!base || !id || !targetFlowId) return '';

  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}previewSessionId=${encodeURIComponent(id)}&flowId=${encodeURIComponent(targetFlowId)}`;
}

export default function Flows({ token, roles = [], orgId }) {
  const roleList = Array.isArray(roles) ? roles : [];
  const isSystemAdmin = roleList.some(r => String(r).toLowerCase() === 'systemadministrator');
  const isOrgAdmin = roleList.some(r => String(r).toLowerCase() === 'organizationadministrator');
  const tableRef = useRef(null);
  const fetchSitesAbortRef = useRef(null);
  const fetchFlowsAbortRef = useRef(null);

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(() => getInitialOrgId(isSystemAdmin, orgId));

  useEffect(() => {
    if (!isSystemAdmin && orgId && selectedOrg !== orgId) {
      setSelectedOrg(orgId);
    }
  }, [isSystemAdmin, orgId, selectedOrg]);

  useEffect(() => {
    return () => {
      fetchSitesAbortRef.current?.abort();
      fetchFlowsAbortRef.current?.abort();
    };
  }, []);

  const [sitesForLoad, setSitesForLoad] = useState([]);
  const [selectedSite, setSelectedSite] = useState(() => getInitialSiteId());
  const selectedSiteObj = useMemo(
    () => (sitesForLoad || []).find(s => String(s.id) === String(selectedSite)),
    [sitesForLoad, selectedSite]
  );

  const [flows, setFlows] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchingSites, setFetchingSites] = useState(false);

  const [selectedFlowPreview, setSelectedFlowPreview] = useState(null);

  const [confirmation, setConfirmation] = useState(null);

  const siteIdsFromToken = (() => {
    try {
      if (!token) return [];
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      return (payload?.site_ids || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    } catch (e) { return []; }
  })();

  const isOwnedSite = (siteId) => siteIdsFromToken.includes(String(siteId).toLowerCase());

  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const parseFlowListError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      400: 'Invalid flow query. Please verify the selected site and organization context.',
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view flows for this site.',
      404: 'No user flows found for the specified site.',
      500: 'An error occurred while retrieving flows for this site.',
      default: 'Failed to fetch flows.'
    });
  }, []);

  const parseFlowPreviewError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      400: 'Invalid preview request for this flow.',
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to preview this flow.',
      404: 'User flow not found for the specified organization.',
      500: 'An unexpected error occurred while loading flow preview.',
      default: 'Unable to load flow preview.'
    });
  }, []);

  const parseFlowActionError = useCallback((res, type) => {
    const fallbackByType = {
      DELETE: 'Failed to delete flow.',
      APPROVE: 'Failed to approve flow.',
      REJECT: 'Failed to reject flow.',
      DISABLE: 'Failed to disable flow.'
    };

    return readAdminApiErrorMessage(res, {
      400: 'Invalid workflow data or action for this flow operation.',
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to perform this flow action.',
      404: 'The requested flow could not be found.',
      409: type === 'DELETE'
        ? 'Flow cannot be deleted while visibilities exist for this flow.'
        : 'This workflow action conflicts with the current flow state.',
      500: 'An unexpected server error occurred while processing the flow action.',
      default: fallbackByType[type] || 'Flow action failed.'
    });
  }, []);

  const parseOrganizationError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view organizations.',
      500: 'An error occurred while loading organizations.',
      default: 'Failed to load organizations.'
    });
  }, []);

  const parseSiteCollectionsError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view site collections.',
      404: 'Site collections for the selected organization were not found.',
      500: 'An error occurred while loading site collections.',
      default: 'Could not load site collections.'
    });
  }, []);

  const loadFlows = useCallback(async () => {
    if (!selectedSite) {
      showToast.warning("Please select a Site Collection first.");
      return;
    }
    fetchFlowsAbortRef.current?.abort();
    const controller = new AbortController();
    fetchFlowsAbortRef.current = controller;

    setLoading(true);
    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    try {
      const res = await fetch(`${API_BASE_URL}/userflows/site-collections/${selectedSite}?organizationId=${orgIdToUse}`, {
        headers: getHeaders(),
        signal: controller.signal
      });

      if (!res.ok) {
        const message = await parseFlowListError(res);
        if (res.status === 404 && /No user flows found for the specified site/i.test(message)) {
          setFlows([]);
          setCurrentPage(1);
          return;
        }

        throw new Error(message);
      }

      const data = await res.json().catch(() => []);
      if (typeof data === 'string' && /No user flows found for the specified site/i.test(data)) {
        setFlows([]);
      } else {
        setFlows(Array.isArray(data) ? data : []);
      }
      setCurrentPage(1);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      showToast.error(err.message || 'Failed to fetch flows.');
      setFlows([]);
    } finally {
      if (fetchFlowsAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [selectedSite, isSystemAdmin, selectedOrg, orgId, getHeaders, parseFlowListError]);

  useEffect(() => {
    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    if (orgIdToUse && selectedSite) {
      loadFlows();
    } else {
      fetchFlowsAbortRef.current?.abort();
      setFlows([]);
    }
  }, [selectedSite, selectedOrg, orgId, isSystemAdmin, loadFlows]);

  const handleView = async (flow) => {
    try {
      const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
      const res = await fetch(`${API_BASE_URL}/userflows/organizations/${orgIdToUse}/site-collections/${selectedSite}/preview/${flow.id}`, {
        method: 'GET',
        headers: getHeaders(),
      });
      if (!res.ok) {
        const message = await parseFlowPreviewError(res);
        throw new Error(message);
      }
      const data = await res.json();
      setSelectedFlowPreview(data);
    } catch (err) {
      showToast.error(err?.message || 'Unable to load flow view.');
    }
  };

  const handlePreview = async (flow) => {
    try {
      if (!selectedSite) {
        showToast.warning("Please select a Site Collection first.");
        return;
      }

      const baseUrl = getHostUrlFromSite(selectedSiteObj);
      if (!baseUrl) {
        showToast.warning("Preview requires SiteCollection Preview Base URL or at least one Domain Name.");
        return;
      }

      const createRes = await fetch(
        `${API_BASE_URL}/site-collections/${selectedSite}/preview-sessions`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(PREVIEW_SESSION_CONFIG)
        }
      );

      if (!createRes.ok) {
        const message = await parseFlowPreviewError(createRes);
        throw new Error(message);
      }

      const createJson = await createRes.json();
      const previewSessionId = createJson?.previewSessionId;

      if (!previewSessionId) {
        throw new Error('previewSessionId missing from preview session response');
      }

      const finalUrl = buildPreviewRedirectUrl({
        previewBaseUrl: baseUrl,
        previewSessionId,
        flowId: flow.id
      });

      if (!finalUrl) {
        throw new Error('Unable to build preview redirect URL (missing baseUrl or previewSessionId).');
      }

      const opened = window.open(finalUrl, '_blank', 'noopener,noreferrer');

      if (!opened) {
        showToast.info('Preview launched. If no new tab opened, please allow pop-ups for this site and retry.');
      }
    } catch (err) {
      showToast.error(err?.message || 'Unable to load flow preview.');
    }
  };

  const executeAction = async () => {
    if (!confirmation) return;

    const { id, type } = confirmation;
    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;

    setSubmitting(true);

    try {
      if (type === 'DELETE') {
        const res = await fetch(`${API_BASE_URL}/userflows/${id}?organizationId=${orgIdToUse}`, {
          method: 'DELETE',
          headers: getHeaders()
        });

        if (!res.ok) {
          const message = await parseFlowActionError(res, type);
          throw new Error(message);
        }

        showToast.success('Flow deleted successfully.');
      } else if (type === 'APPROVE' || type === 'REJECT') {
        const apiAction = mapWorkflowActionToApi(type);

        const res = await fetch(`${API_BASE_URL}/workflows/approve-or-reject?organizationId=${orgIdToUse}`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({
            artifactType: 'Flow',
            action: apiAction,
            artifactId: id
          })
        });

        if (!res.ok) {
          const message = await parseFlowActionError(res, type);
          throw new Error(message);
        }

        showToast.success(`Flow ${type.toLowerCase()}d successfully.`);
      } else if (type === 'DISABLE') {
        const res = await fetch(`${API_BASE_URL}/workflows/disable?organizationId=${orgIdToUse}`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({
            artifactType: 'Flow',
            action: mapWorkflowActionToApi(type),
            artifactId: id
          })
        });

        if (!res.ok) {
          const message = await parseFlowActionError(res, type);
          throw new Error(message);
        }

        showToast.success('Flow disabled successfully.');
      }

      setConfirmation(null);
      await loadFlows();
    } catch (err) {
      showToast.error(err?.message || 'Unable to complete flow action.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSystemAdmin) {
      fetch(`${API_BASE_URL}/organizations`, { headers: getHeaders() })
        .then(async (res) => {
          if (!res.ok) {
            const message = await parseOrganizationError(res);
            throw new Error(message);
          }

          return res.json().catch(() => []);
        })
        .then((data) => {
          const list = Array.isArray(data) ? data : [];
          setOrganizations(list);
          setSelectedOrg((prev) => validateAndReconcileSelection(list, prev, isSystemAdmin ? ORG_STORAGE_KEY : null));
        })
        .catch((e) => showToast.error(`Failed to load organizations: ${e.message}`));
    }
  }, [isSystemAdmin, getHeaders, parseOrganizationError]);

  useEffect(() => {
    const orgToUse = isSystemAdmin ? selectedOrg : orgId;
    if (orgToUse) {
      setFetchingSites(true);
      fetchSitesAbortRef.current?.abort();
      const controller = new AbortController();
      fetchSitesAbortRef.current = controller;

      fetch(`${API_BASE_URL}/site-collections?organizationId=${orgToUse}`, {
        headers: getHeaders(),
        signal: controller.signal
      })
        .then(async (res) => {
          if (!res.ok) {
            const message = await parseSiteCollectionsError(res);
            throw new Error(message);
          }

          return res.json().catch(() => []);
        })
        .then(data => {
          const list = Array.isArray(data) ? data : [];
          setSitesForLoad(list);
          setSelectedSite((prev) => validateAndReconcileSelection(list, prev, SITE_STORAGE_KEY));
        })
        .catch((err) => {
          if (err?.name === 'AbortError') return;
          setSitesForLoad([]);
          setSelectedSite('');
          showToast.error(err?.message || 'Could not load site collections.');
        })
        .finally(() => {
          if (fetchSitesAbortRef.current === controller) {
            setFetchingSites(false);
          }
        });
    } else {
      fetchSitesAbortRef.current?.abort();
      setSitesForLoad([]);
      setSelectedSite('');
      setFetchingSites(false);
    }
  }, [selectedOrg, isSystemAdmin, orgId, getHeaders, parseSiteCollectionsError]);

  useEffect(() => {
    setCurrentPage(1);
    setStatusFilter('All');
    setSearchTerm('');
  }, [selectedSite, selectedOrg]);

  const filteredFlows = useMemo(() => {
    return flows.filter(f => {
      const searchQuery = (searchTerm ?? '').toLowerCase().trim();
      const matchesSearch = !searchQuery
        || (f.flowName ?? '').toLowerCase().includes(searchQuery)
        || String(f.id ?? '').toLowerCase().includes(searchQuery);
      let matchesStatus = statusFilter === 'All';
      if (statusFilter === 'Pending') matchesStatus = f.status === 'Pending';
      else if (statusFilter === 'Submitted') matchesStatus = f.status === 'Submitted';
      else if (statusFilter === 'Approved') matchesStatus = f.status === 'Approved';
      else if (statusFilter === 'Rejected') matchesStatus = f.status === 'Rejected';
      else if (statusFilter === 'Disabled') matchesStatus = f.status === 'Disabled';
      return matchesSearch && matchesStatus;
    });
  }, [flows, searchTerm, statusFilter]);

  const totalPages = Math.ceil(filteredFlows.length / itemsPerPage);
  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredFlows.slice(start, start + itemsPerPage);
  }, [filteredFlows, currentPage]);

  const visiblePages = useMemo(() => {
    const maxVisible = 5;
    if (totalPages <= maxVisible) return Array.from({ length: totalPages }, (_, i) => i + 1);
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;
    if (end > totalPages) { end = totalPages; start = end - maxVisible + 1; }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  const getBadgeStyle = (status) => {
    const base = {
      padding: '6px 12px',
      borderRadius: '8px',
      fontSize: '11px',
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      transition: 'all 0.2s ease'
    };

    if (status === 'Approved') return { ...base, background: '#dcfce7', color: '#166534' };
    if (status === 'Rejected') return { ...base, background: '#fee2e2', color: '#991b1b' };
    if (status === 'Submitted') return { ...base, background: '#e9d5ff', color: '#581c87' };
    if (status === 'Pending') return { ...base, background: '#fef3c7', color: '#92400e' };
    if (status === 'Disabled') {
      return { ...base, background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db', fontWeight: 600, opacity: 0.85 };
    }
    return base;
  };

  return (
    <div style={wrapperStyle}>
      <div style={containerStyle}>
        <header style={headerStyle}>
          <div style={headerContentStyle}>
            <div style={headerBadgeStyle}><FaProjectDiagram size={22} /></div>
            <div><h1 style={titleStyle}>Flow Management</h1></div>
          </div>
        </header>

        <div style={filtersCardStyle}>
          <div style={cardHeaderStyle}><FaFilter color="#6366f1" /><span style={cardTitleStyle}>Selection Criteria</span></div>
          <div style={filtersGridStyle}>
            {isSystemAdmin && (
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Organization</label>
                <SearchableDropdown
                  options={organizations}
                  selectedValue={selectedOrg}
                  onSelect={(value) => {
                    setSelectedOrg(value);
                    persistOrgSelection(isSystemAdmin, value);
                    setSelectedSite('');
                    if (value && selectedSite) {
                      trackEvent('Dropdown Selected', {
                        section: 'flows',
                        organizationId: value,
                        siteCollectionId: selectedSite
                      });
                    }
                  }}
                  placeholder="Choose Organization"
                  displayKey="name"
                  valueKey="id"
                  styles={{
                    container: dropdownContainerStyle,
                    trigger: dropdownTriggerStyle,
                    triggerText: dropdownTriggerTextStyle,
                    menu: dropdownMenuStyle,
                    searchContainer: searchBoxContainerStyle,
                    searchIcon: searchIconStyle,
                    searchInput: dropdownSearchInputStyle,
                    list: dropdownListStyle,
                    option: dropdownOptionStyle,
                    optionText: optionTextStyle,
                    optionName: optionNameStyle,
                    optionId: optionIdStyle,
                    emptyOption: emptyOptionStyle
                  }}
                />
              </div>
            )}

            <div style={inputGroupStyle}>
              <label style={labelStyle}>Site Collection</label>
              <SearchableDropdown
                options={sitesForLoad}
                selectedValue={selectedSite}
                onSelect={(value) => {
                  setSelectedSite(value);
                  persistSiteSelection(value);
                  const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
                  if (value && orgIdToUse) {
                    trackEvent('Dropdown Selected', {
                      section: 'flows',
                      organizationId: orgIdToUse,
                      siteCollectionId: value
                    });
                  }
                }}
                placeholder={isSystemAdmin && !selectedOrg ? 'Select Org First' : 'Choose Site'}
                displayKey="name"
                valueKey="id"
                isLoading={fetchingSites}
                disabled={fetchingSites || (isSystemAdmin && !selectedOrg)}
                emptyMessage={sitesForLoad.length === 0 ? 'No sites available' : 'No results found'}
                styles={{
                  container: dropdownContainerStyle,
                  trigger: dropdownTriggerStyle,
                  triggerText: dropdownTriggerTextStyle,
                  menu: dropdownMenuStyle,
                  searchContainer: searchBoxContainerStyle,
                  searchIcon: searchIconStyle,
                  searchInput: dropdownSearchInputStyle,
                  list: dropdownListStyle,
                  option: dropdownOptionStyle,
                  optionText: optionTextStyle,
                  optionName: optionNameStyle,
                  optionId: optionIdStyle,
                  emptyOption: emptyOptionStyle
                }}
              />
            </div>

            <div style={{ ...inputGroupStyle, justifyContent: 'flex-end' }}>
              <button
                onClick={loadFlows}
                disabled={!selectedSite || loading || submitting}
                style={{
                  ...primaryButtonStyle,
                  opacity: !selectedSite || loading || submitting ? 0.6 : 1,
                  cursor: !selectedSite || loading || submitting ? 'not-allowed' : 'pointer'
                }}
              >
                <FaSyncAlt style={{ animation: (loading || submitting) ? 'spin 1s linear infinite' : 'none' }} />
                {(loading || submitting) ? 'Syncing...' : 'Sync Flows'}
              </button>
            </div>
          </div>
        </div>

        <div ref={tableRef} style={tableCardStyle}>
          <div style={tableActionsHeader}>
            <div style={tableToolbarGrid}>
              <div style={searchWrapper}>
                <FaSearch style={searchIcon} />
                <input
                  style={searchInput}
                  placeholder="Search flows..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>
            </div>
          </div>

          <table style={tableStyle}>
            <thead>
              <tr style={tableHeaderRowStyle}>
                <th style={thStyle}>Flow Experience</th>
                <th style={thStyle}>Status</th>
                <th style={thActionStyle}>Action Control</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="3" style={emptyStateStyle}><FaSyncAlt className="spin" /> Syncing with Server...</td></tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="3" style={emptyStateStyle}>
                    <FaFolderOpen size={32} opacity={0.3} />
                    <div style={{ marginTop: 10 }}>
                      {isSystemAdmin && !selectedOrg
                        ? 'Select an organization to begin'
                        : !selectedSite
                          ? 'Select a site to begin'
                          : 'No flows found'}
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((flow) => (
                  <tr key={flow.id} style={trStyle}>
                    <td style={tdStyle}>
                      <div style={flowNameText}>{flow.flowName}</div>
                      <div style={flowIdText}>ID: {flow.id}</div>
                    </td>
                    <td style={tdStyle}>
                      <span style={getBadgeStyle(flow.status)}>
                        <FaCircle size={7} />
                        {flow.status}
                      </span>
                    </td>

                    <td style={tdActionStyle}>
                      {confirmation?.id === flow.id ? (
                        <div style={inlineConfirmWrapper}>
                          <FaQuestionCircle color="#6366f1" />
                          <span style={confirmText}>
                            Confirm {confirmation.type === "APPROVE"
                              ? "Approve"
                              : confirmation.type === "REJECT"
                                ? "Reject"
                                : confirmation.type === "DISABLE"
                                  ? "Disable"
                                  : "Delete"}{" "}
                            flow?
                          </span>
                          <button style={btnYes} onClick={executeAction} disabled={loading || submitting}>Yes</button>
                          <button style={btnNo} onClick={() => setConfirmation(null)}>No</button>
                        </div>
                      ) : (
                        <div style={actionButtonGroupStyle}>
                          <button
                            title="View (Modal)"
                            style={btnView}
                            onClick={() => handleView(flow)}
                          >
                            <FaEye /> View
                          </button>

                          <button
                            title="Preview (New Tab)"
                            style={btnPreviewNewTab}
                            onClick={() => handlePreview(flow)}
                          >
                            <MdPreview /> Preview
                          </button>

                          {flow.status === "Submitted" && (isSystemAdmin || isOrgAdmin || isOwnedSite(flow.siteId)) && (
                            <>
                              <button title="Approve" style={actionBtnApprove} onClick={() => setConfirmation({ id: flow.id, type: 'APPROVE' })}><FaCheck /></button>
                              <button title="Reject" style={actionBtnReject} onClick={() => setConfirmation({ id: flow.id, type: 'REJECT' })}><FaTimes /></button>
                            </>
                          )}

                          {flow.status === "Approved" && (isSystemAdmin || isOrgAdmin) && (
                            <button title="Disable" style={actionBtnDisable} onClick={() => setConfirmation({ id: flow.id, type: 'DISABLE' })}><FaBan size={12} /></button>
                          )}

                          {(isSystemAdmin || isOrgAdmin) && (
                            <button title="Delete" style={actionBtnDelete} onClick={() => setConfirmation({ id: flow.id, type: 'DELETE' })}><FaTrash size={12} /></button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={paginationRow}>
              <div style={paginationInfoStyle}>Showing <b>{currentItems.length}</b> of <b>{filteredFlows.length}</b></div>
              <div style={paginationControlsWrapper}>
                <button style={pageArrow} disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}><FaChevronLeft /></button>
                {visiblePages.map(page => (
                  <button key={page} onClick={() => setCurrentPage(page)} style={{ ...pageNumberBtn, ...(currentPage === page ? activePage : {}) }}>{page}</button>
                ))}
                <button style={pageArrow} disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}><FaChevronRight /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedFlowPreview && <FlowPreviewModal flow={selectedFlowPreview} siteCollectionId={selectedSite} onClose={() => setSelectedFlowPreview(null)} />}
    </div>
  );
}

const btnView = {
  width: 'auto',
  padding: '0 12px',
  height: 32,
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  background: '#ede9fe',
  color: '#6366f1',
  fontSize: 11,
  fontWeight: 800
};

const btnPreviewNewTab = {
  width: 'auto',
  padding: '0 12px',
  height: 32,
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  background: '#e0f2fe',
  color: '#0369a1',
  fontSize: 11,
  fontWeight: 800
};

const dropdownContainerStyle = { position: 'relative' };
const dropdownTriggerStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontWeight: 500, color: '#1e293b', transition: 'all 0.2s ease' };
const dropdownTriggerTextStyle = { flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const dropdownMenuStyle = { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, marginTop: 4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', zIndex: 1000, overflow: 'hidden', animation: 'fadeIn 0.2s ease-out' };
const searchBoxContainerStyle = { position: 'relative', padding: '8px 12px', borderBottom: '1px solid #f1f5f9', background: '#fcfdfe' };
const searchIconStyle = { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12, pointerEvents: 'none' };
const dropdownSearchInputStyle = { width: '100%', padding: '8px 12px 8px 28px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff' };
const dropdownListStyle = { maxHeight: '240px', overflowY: 'auto', padding: '4px' };
const dropdownOptionStyle = { width: '100%', padding: '10px 12px', border: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 6, margin: '2px 0', transition: 'all 0.2s ease', fontSize: 13 };
const optionTextStyle = { flex: 1 };
const optionNameStyle = { fontWeight: 600, color: 'inherit' };
const optionIdStyle = { fontSize: '11px', opacity: 0.6, marginTop: 2 };
const emptyOptionStyle = { padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 13 };

const wrapperStyle = { width: '100%', minHeight: '100vh', background: '#f8fafc', paddingBottom: 60, fontFamily: 'Inter, sans-serif' };
const containerStyle = { maxWidth: 1200, margin: '0 auto', padding: '24px' };
const headerStyle = { marginBottom: 32 };
const headerContentStyle = { display: 'flex', alignItems: 'center', gap: 16 };
const headerBadgeStyle = { width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' };
const titleStyle = { fontSize: 24, fontWeight: 800, margin: 0, color: '#0f172a' };

const filtersCardStyle = { background: '#fff', borderRadius: 16, padding: 24, marginBottom: 24, border: '1px solid #e2e8f0' };
const cardHeaderStyle = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, borderBottom: '1px solid #f1f5f9', paddingBottom: 12 };
const cardTitleStyle = { fontSize: 15, fontWeight: 700 };
const filtersGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, alignItems: 'end' };
const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelStyle = { fontSize: 11, fontWeight: 600, color: '#64748b' };

const primaryButtonStyle = { background: '#6366f1', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 };

const tableCardStyle = { background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' };
const tableActionsHeader = { padding: '16px 20px', borderBottom: '1px solid #f1f5f9', background: '#fcfdfe' };
const tableToolbarGrid = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };

const searchWrapper = { position: 'relative', width: 250 };
const searchIcon = { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };

const searchInput = {
  width: '100%',
  padding: '8px 12px 8px 36px',
  borderRadius: 8,
  border: '1.5px solid #e2e8f0',
  fontSize: 13,
  outline: 'none',
  background: '#fff',
  color: '#3a4557'
};

const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const tableHeaderRowStyle = { background: '#f8fafc' };
const thStyle = { padding: '14px 20px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'left' };
const thActionStyle = { ...thStyle, textAlign: 'right' };
const trStyle = { borderBottom: '1px solid #f1f5f9' };
const tdStyle = { padding: '16px 20px', fontSize: 14 };
const tdActionStyle = { padding: '16px 20px', textAlign: 'right' };
const flowNameText = { fontWeight: 700, color: '#1e293b' };
const flowIdText = { fontSize: '10px', color: '#94a3b8' };

const actionButtonGroupStyle = { display: 'inline-flex', gap: 8 };
const actionBtnBase = { width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const actionBtnApprove = { ...actionBtnBase, background: '#dcfce7', color: '#166534' };
const actionBtnReject = { ...actionBtnBase, background: '#fee2e2', color: '#991b1b' };
const actionBtnDisable = { ...actionBtnBase, background: '#f3e8ff', color: '#7c3aed' };
const actionBtnDelete = { ...actionBtnBase, background: '#f1f5f9', color: '#64748b' };

const emptyStateStyle = { padding: '60px 20px', textAlign: 'center', color: '#94a3b8' };

const paginationRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: '#fff', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 16 };
const paginationInfoStyle = { fontSize: 14, color: '#64748b', fontWeight: 500 };
const paginationControlsWrapper = { display: 'flex', alignItems: 'center', gap: 8 };
const pageNumberBtn = { minWidth: 36, height: 36, padding: '0 4px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: '700', cursor: 'pointer', color: '#64748b' };
const activePage = { background: '#6366f1', color: '#fff', borderColor: '#6366f1', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)' };
const pageArrow = { ...pageNumberBtn, display: 'flex', alignItems: 'center', justifyContent: 'center' };

const inlineConfirmWrapper = { display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '4px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', animation: 'fadeIn 0.2s ease-out' };
const confirmText = { fontSize: '12px', fontWeight: '700', color: '#475569' };
const btnYes = { background: '#6366f1', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' };
const btnNo = { background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' };